import { createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import toast from "react-hot-toast";
import {
  AddWarehousePayload,
  AddWarehouseResponse,
  AccountOfficerDashboardStats,
  AccountOfficerAssignedCustomersResponse,
  AccountOfficerOrdersOverviewItem,
  AccountOfficerOverview,
  AccountOfficerRevenueTrend,
  AccountOfficerTopCustomer,
  CreateStatePayload,
  CreateStateResponse,
  PeriodFilter,
  State,
  UpdateStorePayload,
  UpdateStoreResponse,
  AccountOfficerCustomerActivity,
  AccountOfficerCustomerOrderHistoryResponse,
  CartItem,
  CartItemsResponse,
  AddToCartRequest,
  UpdateCartItemRequest,
  DeleteCartItemRequest,
  OtpRequest,
  VerifyOtpRequest,
  CreateOrderRequest,
  CreateOrderResponse,
  UpdateDeliveryPrefsPayload,
  FeeItem,
  FeesResponse,
  CheckOrderResponse,
  CustomerDetailResponse,
  ValidatePromoCodeRequest,
  ValidatePromoCodeResponse,
} from "../types/types";

export const login = createAsyncThunk(
  "login",
  async (credentials: { email: string; password: string }, thunkAPI) => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/admins/login`,
        credentials,
      );
      sessionStorage.setItem("adminLevel", response.data.admin.adminLevel);

      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(
        error?.response?.data?.message ||
          error?.response?.data?.error ||
          error?.message ||
          "Login failed",
      );
    }
  },
);

export const updateDeliveryPreferencesThunk = createAsyncThunk<
  any,
  { customeruserId: string; data: UpdateDeliveryPrefsPayload },
  { rejectValue: string }
>(
  "order/updateDeliveryPreferences",
  async ({ customeruserId, data }, thunkAPI) => {
    const auth: any = sessionStorage.getItem("authState");
    const jsonData = JSON.parse(auth || "{}");
    const token = jsonData?.token;

    try {
      const res = await axios.put(
        `${process.env.REACT_APP_API_URL}/updateDeliveryPreferencesU/${customeruserId}`,
        data,
        {
          headers: { Authorization: token },
        },
      );
      return res.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(
        error.response?.data?.message || error.message,
      );
    }
  },
);

export const getAllFees = createAsyncThunk<
  FeesResponse,
  void,
  { rejectValue: string }
>("order/getAllFees", async (_, thunkAPI) => {
  const auth: any = sessionStorage.getItem("authState");
  const jsonData = JSON.parse(auth || "{}");
  const token = jsonData?.token;

  try {
    const res = await axios.get(
      `${process.env.REACT_APP_API_URL}/get-allFees`,
      {
        headers: { Authorization: token },
      },
    );
    return res.data;
  } catch (error: any) {
    return thunkAPI.rejectWithValue(
      error.response?.data?.message || error.message,
    );
  }
});

export const checkIfUserHasOrder = createAsyncThunk<
  CheckOrderResponse,
  string, // customeruserId
  { rejectValue: string }
>("order/checkIfUserHasOrder", async (customeruserId, thunkAPI) => {
  const auth: any = sessionStorage.getItem("authState");
  const jsonData = JSON.parse(auth || "{}");
  const token = jsonData?.token;

  try {
    const res = await axios.get(
      `${process.env.REACT_APP_API_URL}/checkIfUserHasOrderByUserId/${customeruserId}`,
      {
        headers: { Authorization: token },
      },
    );
    return res.data;
  } catch (error: any) {
    return thunkAPI.rejectWithValue(
      error.response?.data?.message || error.message,
    );
  }
});

export const getCustomerByuserIdThunk = createAsyncThunk<
  CustomerDetailResponse,
  string, // customeruserId
  { rejectValue: string }
>("order/getCustomerByuserId", async (customeruserId, thunkAPI) => {
  const auth: any = sessionStorage.getItem("authState");
  const jsonData = JSON.parse(auth || "{}");
  const token = jsonData?.token;

  try {
    const res = await axios.get(
      `${process.env.REACT_APP_API_URL}/getCustomerByuserId/${customeruserId}`,
      {
        headers: { Authorization: token },
      },
    );
    return res.data;
  } catch (error: any) {
    return thunkAPI.rejectWithValue(
      error.response?.data?.message || error.message,
    );
  }
});

export const validatePromoCodeThunk = createAsyncThunk<
  ValidatePromoCodeResponse,
  { customerId: string; data: ValidatePromoCodeRequest },
  { rejectValue: string }
>("order/validatePromoCode", async ({ customerId, data }, thunkAPI) => {
  const auth: any = sessionStorage.getItem("authState");
  const jsonData = JSON.parse(auth || "{}");
  const token = jsonData?.token;

  try {
    const res = await axios.post(
      `${process.env.REACT_APP_API_URL}/user/gamification/promo-code/validate-by-user/${customerId}`,
      data,
      {
        headers: { Authorization: token },
      },
    );
    return res.data;
  } catch (error: any) {
    return thunkAPI.rejectWithValue(
      error.response?.data?.message || error.message,
    );
  }
});

export const getAccountOfficerDashboardStats = createAsyncThunk<
  AccountOfficerDashboardStats,
  { period?: string },
  { rejectValue: string }
>("accountOfficer/getDashboardStats", async ({ period }, thunkAPI) => {
  const auth: any = sessionStorage.getItem("authState");
  const jsonData = JSON.parse(auth || "{}");
  const token = jsonData?.token;

  if (!token) {
    return thunkAPI.rejectWithValue("Authentication token not found.");
  }

  const queryPeriod = period || new Date().toISOString().slice(0, 7); // Default to current YYYY-MM

  try {
    const res = await axios.get(
      `${process.env.REACT_APP_API_URL}/admin/kpi/kpi-summary?period=${queryPeriod}`,
      {
        headers: {
          Authorization: token,
        },
      },
    );
    return res.data.data as AccountOfficerDashboardStats;
  } catch (error: any) {
    return thunkAPI.rejectWithValue(
      error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        "Failed to fetch dashboard stats",
    );
  }
});

export const getAccountOfficerOverview = createAsyncThunk<
  AccountOfficerOverview,
  void,
  { rejectValue: string }
>("accountOfficer/getOverview", async (_, thunkAPI) => {
  const auth: any = sessionStorage.getItem("authState");
  const jsonData = JSON.parse(auth || "{}");
  const token = jsonData?.token;

  if (!token) {
    return thunkAPI.rejectWithValue("Authentication token not found.");
  }

  try {
    const res = await axios.get(
      `${process.env.REACT_APP_API_URL}/account-officer/overview`,
      {
        headers: {
          Authorization: token,
        },
      },
    );
    return res.data.data as AccountOfficerOverview;
  } catch (error: any) {
    return thunkAPI.rejectWithValue(
      error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        "Failed to fetch overview",
    );
  }
});

export const getAccountOfficerRevenueTrend = createAsyncThunk<
  AccountOfficerRevenueTrend,
  { period: PeriodFilter },
  { rejectValue: string }
>("accountOfficer/getRevenueTrend", async ({ period }, thunkAPI) => {
  const auth: any = sessionStorage.getItem("authState");
  const jsonData = JSON.parse(auth || "{}");
  const token = jsonData?.token;

  if (!token) {
    return thunkAPI.rejectWithValue("Authentication token not found.");
  }

  try {
    const res = await axios.get(
      `${process.env.REACT_APP_API_URL}/account-officer/revenue-trend?period=${period}`,
      {
        headers: {
          Authorization: token,
        },
      },
    );
    return res.data.data as AccountOfficerRevenueTrend;
  } catch (error: any) {
    return thunkAPI.rejectWithValue(
      error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        "Failed to fetch revenue trend",
    );
  }
});

export const getAccountOfficerOrdersOverview = createAsyncThunk<
  { period: PeriodFilter; data: AccountOfficerOrdersOverviewItem[] },
  { period: PeriodFilter },
  { rejectValue: string }
>("accountOfficer/getOrdersOverview", async ({ period }, thunkAPI) => {
  const auth: any = sessionStorage.getItem("authState");
  const jsonData = JSON.parse(auth || "{}");
  const token = jsonData?.token;

  if (!token) {
    return thunkAPI.rejectWithValue("Authentication token not found.");
  }

  try {
    const res = await axios.get(
      `${process.env.REACT_APP_API_URL}/account-officer/orders-overview?period=${period}`,
      {
        headers: {
          Authorization: token,
        },
      },
    );
    const data = (res.data.data || []) as AccountOfficerOrdersOverviewItem[];
    return { period, data };
  } catch (error: any) {
    return thunkAPI.rejectWithValue(
      error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        "Failed to fetch orders overview",
    );
  }
});

export const getAccountOfficerTopCustomersByRevenue = createAsyncThunk<
  AccountOfficerTopCustomer[],
  void,
  { rejectValue: string }
>("accountOfficer/getTopCustomersByRevenue", async (_, thunkAPI) => {
  const auth: any = sessionStorage.getItem("authState");
  const jsonData = JSON.parse(auth || "{}");
  const token = jsonData?.token;

  if (!token) {
    return thunkAPI.rejectWithValue("Authentication token not found.");
  }

  try {
    const res = await axios.get(
      `${process.env.REACT_APP_API_URL}/account-officer/top-customers`,
      {
        headers: {
          Authorization: token,
        },
      },
    );
    return (res.data.data || []) as AccountOfficerTopCustomer[];
  } catch (error: any) {
    return thunkAPI.rejectWithValue(
      error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        "Failed to fetch top customers",
    );
  }
});

export const getAccountOfficerAssignedCustomersWithRisk = createAsyncThunk<
  AccountOfficerAssignedCustomersResponse,
  { page?: number; limit?: number; search?: string; filter?: string; accountType?: string },
  { rejectValue: string }
>(
  "accountOfficer/getAssignedCustomersWithRisk",
  async ({ page, limit, search, filter, accountType }, thunkAPI) => {
    const auth: any = sessionStorage.getItem("authState");
    const jsonData = JSON.parse(auth || "{}");
    const token = jsonData?.token;

    if (!token) {
      return thunkAPI.rejectWithValue("Authentication token not found.");
    }

    const queryPage = page ?? 1;
    const queryLimit = limit ?? 10;
    const querySearch = search ?? "";
    const filterParam = filter ? `&filter=${filter}` : "";
    const accountTypeParam = accountType ? `&accountType=${accountType}` : "";

    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/account-officer/customers/assigned?page=${queryPage}&limit=${queryLimit}&search=${querySearch}${filterParam}${accountTypeParam}`,
        {
          headers: {
            Authorization: token,
          },
        },
      );
      return {
        page: res.data.page,
        limit: res.data.limit,
        total: res.data.total,
        totalPages: res.data.totalPages,
        data: res.data.data || [],
      } as AccountOfficerAssignedCustomersResponse;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(
        error?.response?.data?.message ||
          error?.response?.data?.error ||
          error?.message ||
          "Failed to fetch assigned customers",
      );
    }
  },
);

export const getAccountOfficerCustomerActivity = createAsyncThunk<
  AccountOfficerCustomerActivity[],
  void,
  { rejectValue: string }
>("accountOfficer/getCustomerActivity", async (_, thunkAPI) => {
  const auth: any = sessionStorage.getItem("authState");
  const jsonData = JSON.parse(auth || "{}");
  const token = jsonData?.token;

  if (!token) {
    return thunkAPI.rejectWithValue("Authentication token not found.");
  }

  try {
    const res = await axios.get(
      `${process.env.REACT_APP_API_URL}/account-officer/customer-realtime-activity`,
      {
        headers: {
          Authorization: token,
        },
      },
    );
    return (res.data.activities || []) as AccountOfficerCustomerActivity[];
  } catch (error: any) {
    return thunkAPI.rejectWithValue(
      error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        "Failed to fetch customer activity",
    );
  }
});

export const getAccountOfficerCustomerOrderHistory = createAsyncThunk<
  AccountOfficerCustomerOrderHistoryResponse,
  string,
  { rejectValue: string }
>("accountOfficer/getCustomerOrderHistory", async (customerId, thunkAPI) => {
  const auth: any = sessionStorage.getItem("authState");
  const jsonData = JSON.parse(auth || "{}");
  const token = jsonData?.token;

  if (!token) {
    return thunkAPI.rejectWithValue("Authentication token not found.");
  }

  try {
    const res = await axios.get(
      `${process.env.REACT_APP_API_URL}/account-officer/assigned-customers-order-history/${customerId}`,
      {
        headers: {
          Authorization: token,
        },
      },
    );
    return res.data.data as AccountOfficerCustomerOrderHistoryResponse;
  } catch (error: any) {
    return thunkAPI.rejectWithValue(
      error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        "Failed to fetch customer order history",
    );
  }
});

// Sales Admin Authentication Thunks
export const salesAdminRegister = createAsyncThunk(
  "salesAdminRegister",
  async (
    data: {
      firstName: string;
      lastName: string;
      email: string;
      region: string;
    },
    thunkAPI,
  ) => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/sales-admin/register`,
        data,
      );
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(
        error.response.data.error || "Registration failed",
      );
    }
  },
);

export const salesAdminChangePassword = createAsyncThunk(
  "salesAdminChangePassword",
  async (
    data: { currentPassword: string; newPassword: string; userId: string },
    thunkAPI,
  ) => {
    try {
      const auth: any = sessionStorage.getItem("authState");
      const jsonData = JSON.parse(auth || "{}");
      const token = jsonData?.token;

      if (!token) {
        return thunkAPI.rejectWithValue("Authentication token not found.");
      }

      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/sales-admin/change-password/${data.userId}`,
        {
          currentPassword: data.currentPassword,
          newPassword: data.newPassword,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(
        error.response.data.error || "Failed to change password",
      );
    }
  },
);

export const salesAdminForgotPassword = createAsyncThunk(
  "salesAdminForgotPassword",
  async (data: { email: string }, thunkAPI) => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/sales-admin/forgot-password`,
        data,
      );
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(
        error.response.data.error ||
          "Failed to process forgot password request",
      );
    }
  },
);

export const salesAdminResetPassword = createAsyncThunk(
  "salesAdminResetPassword",
  async (
    data: { email: string; otp: string; newPassword: string },
    thunkAPI,
  ) => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/sales-admin/reset-password`,
        data,
      );
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(
        error.response.data.error || "Failed to reset password",
      );
    }
  },
);

export const getAllProducts = createAsyncThunk(
  "getAllProducts",
  async (_, thunkAPI) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/get-products`,
      );
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response.data.error);
    }
  },
);

// Cart and Order Thunks
export const getCartItems = createAsyncThunk<
  CartItemsResponse,
  string,
  { rejectValue: string }
>("cart/getItems", async (customerId, thunkAPI) => {
  const auth: any = sessionStorage.getItem("authState");
  const jsonData = JSON.parse(auth || "{}");
  const token = jsonData?.token;

  try {
    const res = await axios.get(
      `${process.env.REACT_APP_API_URL}/ao/getCartItems/${customerId}`,
      {
        headers: { Authorization: token },
      },
    );
    return res.data;
  } catch (error: any) {
    return thunkAPI.rejectWithValue(
      error.response?.data?.message || error.message,
    );
  }
});

export const addToCartThunk = createAsyncThunk<
  { message: string },
  { customerId: string; data: AddToCartRequest },
  { rejectValue: string }
>("cart/add", async ({ customerId, data }, thunkAPI) => {
  const auth: any = sessionStorage.getItem("authState");
  const jsonData = JSON.parse(auth || "{}");
  const token = jsonData?.token;

  try {
    const res = await axios.post(
      `${process.env.REACT_APP_API_URL}/ao/addToCart/${customerId}`,
      data,
      {
        headers: { Authorization: token },
      },
    );
    return res.data;
  } catch (error: any) {
    return thunkAPI.rejectWithValue(
      error.response?.data?.message || error.message,
    );
  }
});

export const updateCartItemThunk = createAsyncThunk<
  { message: string },
  { customerId: string; data: UpdateCartItemRequest },
  { rejectValue: string }
>("cart/update", async ({ customerId, data }, thunkAPI) => {
  const auth: any = sessionStorage.getItem("authState");
  const jsonData = JSON.parse(auth || "{}");
  const token = jsonData?.token;

  try {
    const res = await axios.post(
      `${process.env.REACT_APP_API_URL}/ao/updateCartItem/${customerId}`,
      data,
      {
        headers: { Authorization: token },
      },
    );
    return res.data;
  } catch (error: any) {
    return thunkAPI.rejectWithValue(
      error.response?.data?.message || error.message,
    );
  }
});

export const deleteCartItemThunk = createAsyncThunk<
  { message: string; updatedCart: any },
  { customerId: string; data: DeleteCartItemRequest },
  { rejectValue: string }
>("cart/deleteItem", async ({ customerId, data }, thunkAPI) => {
  const auth: any = sessionStorage.getItem("authState");
  const jsonData = JSON.parse(auth || "{}");
  const token = jsonData?.token;

  try {
    const res = await axios.delete(
      `${process.env.REACT_APP_API_URL}/ao/deleteCartItem/${customerId}`,
      {
        headers: { Authorization: token },
        data,
      },
    );
    return res.data;
  } catch (error: any) {
    return thunkAPI.rejectWithValue(
      error.response?.data?.message || error.message,
    );
  }
});

export const sendConfirmationOtp = createAsyncThunk<
  { message: string; expiresAt: string },
  OtpRequest,
  { rejectValue: string }
>("order/sendOtp", async (data, thunkAPI) => {
  const auth: any = sessionStorage.getItem("authState");
  const jsonData = JSON.parse(auth || "{}");
  const token = jsonData?.token;

  try {
    const res = await axios.post(
      `${process.env.REACT_APP_API_URL}/order/otp/confirmation-otp`,
      data,
      {
        headers: { Authorization: token },
      },
    );
    return res.data;
  } catch (error: any) {
    return thunkAPI.rejectWithValue(
      error.response?.data?.message || error.message,
    );
  }
});

export const verifyOtp = createAsyncThunk<
  { message: string; otpId: string },
  VerifyOtpRequest,
  { rejectValue: string }
>("order/verifyOtp", async (data, thunkAPI) => {
  const auth: any = sessionStorage.getItem("authState");
  const jsonData = JSON.parse(auth || "{}");
  const token = jsonData?.token;

  try {
    const res = await axios.post(
      `${process.env.REACT_APP_API_URL}/order/otp/verify-otp`,
      data,
      {
        headers: { Authorization: token },
      },
    );
    return res.data;
  } catch (error: any) {
    return thunkAPI.rejectWithValue(
      error.response?.data?.message || error.message,
    );
  }
});

export const createOrderThunk = createAsyncThunk<
  CreateOrderResponse,
  { customerId: string; data: CreateOrderRequest },
  { rejectValue: string }
>("order/create", async ({ customerId, data }, thunkAPI) => {
  const auth: any = sessionStorage.getItem("authState");
  const jsonData = JSON.parse(auth || "{}");
  const token = jsonData?.token;

  try {
    const authHeader =
      typeof token === "string" && token.startsWith("Bearer ")
        ? token
        : `Bearer ${token}`;

    const res = await axios.post(
      `${process.env.REACT_APP_API_URL}/orders/ao/${customerId}`,
      data,
      {
        headers: { Authorization: authHeader },
      },
    );
    return res.data;
  } catch (error: any) {
    return thunkAPI.rejectWithValue(
      error.response?.data?.message || error.message,
    );
  }
});

export const getProduct = createAsyncThunk(
  "getProduct",
  async (productId: any, thunkAPI) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/get-product/${productId}`,
      );
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response.data.error);
    }
  },
);

export const getAllProductType = createAsyncThunk(
  "getAllProductType",
  async (_, thunkAPI) => {
    try {
      const response = await axios.get(
        `https://api.dev.foodbank4u.com/foodbank/v1/api/getAllProductTy`,
      );

      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response.data.error);
    }
  },
);

export const updateSingleProduct = createAsyncThunk(
  "updateProduct",
  async ({ data, id }: { data: FormData; id: string }, thunkAPI) => {
    const auth: any = sessionStorage.getItem("authState");
    const jsonData = JSON.parse(auth);
    const token = jsonData.token;

    try {
      const response = await axios.put(
        `${process.env.REACT_APP_API_URL}/update-product/${id}`,
        data,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response.data);
    }
  },
);

export const deleteProductType = createAsyncThunk(
  "deleteProductType",
  async (categoryId: string, thunkAPI) => {
    try {
      const auth: any = sessionStorage.getItem("authState");
      const jsonData = JSON.parse(auth);
      const token = jsonData.token;

      const response = await axios.delete(
        `${process.env.REACT_APP_API_URL}/deleteProductType/${categoryId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response.data);
    }
  },
);

export const deleteProduct = createAsyncThunk(
  "deleteProduct",
  async (productId: string, thunkAPI) => {
    try {
      const auth: any = sessionStorage.getItem("authState");
      const jsonData = JSON.parse(auth);
      const token = jsonData.token;

      const response = await axios.delete(
        `${process.env.REACT_APP_API_URL}/delete-product/${productId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response.data);
    }
  },
);

export const createProductItem = createAsyncThunk(
  "createProductItem",
  async (data: any, thunkAPI) => {
    try {
      const auth: any = sessionStorage.getItem("authState");
      const jsonData = JSON.parse(auth);
      const token = jsonData.token;

      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/create-product`,
        data,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response.data.error);
    }
  },
);
export const createProductType = createAsyncThunk(
  "createProductType",
  async (data: any, thunkAPI) => {
    try {
      const auth: any = sessionStorage.getItem("authState");
      const jsonData = JSON.parse(auth);
      const token = jsonData.token;

      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/createProductType`,
        data,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response.data);
    }
  },
);

export const verificationStageOne = createAsyncThunk(
  "verificationStageOne",
  async (_, thunkAPI) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/verification-stage-one`,
      );

      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response.data);
    }
  },
);

export const getOrders = createAsyncThunk("getOrders", async (_, thunkAPI) => {
  try {
    const auth: any = sessionStorage.getItem("authState");
    const jsonData = JSON.parse(auth);
    const token = jsonData.token;

    const response = await axios.get(
      `${process.env.REACT_APP_API_URL}/getAllOrdersByCustomer`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
    );

    return response.data;
  } catch (error: any) {
    return thunkAPI.rejectWithValue(error.response.data);
  }
});

export const getSingleCustomer = createAsyncThunk(
  "getSingleCustomer",
  async (id: string, thunkAPI) => {
    try {
      const auth: any = sessionStorage.getItem("authState");
      const jsonData = JSON.parse(auth);
      const token = jsonData.token;

      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/getSingleCustomer/${id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response.data);
    }
  },
);

export const getAllRepaymentHistory = createAsyncThunk(
  "getAllRepaymentHistory",
  async (_, thunkAPI) => {
    try {
      const auth: any = sessionStorage.getItem("authState");
      const jsonData = JSON.parse(auth);
      const token = jsonData.token;

      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/getAllRepaymentHistory`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response.data);
    }
  },
);

export const allCustomerNextPay = createAsyncThunk(
  "allCustomerNextPay",
  async (_, thunkAPI) => {
    try {
      const auth: any = sessionStorage.getItem("authState");
      const jsonData = JSON.parse(auth);
      const token = jsonData.token;

      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/getAllStateGovernmentNextPay`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response.data);
    }
  },
);

export const getDefaulters = createAsyncThunk(
  "getDefaulters",
  async (_, thunkAPI) => {
    try {
      const auth: any = sessionStorage.getItem("authState");

      // Handle case where auth might be null or undefined
      if (!auth) {
        console.error("No auth data found in sessionStorage");
        return thunkAPI.rejectWithValue("Authentication token not found");
      }

      const jsonData = JSON.parse(auth);
      console.log("Parsed auth data (Defaulters):", jsonData);

      if (!jsonData || !jsonData.token) {
        console.error("No token found in parsed auth data");
        return thunkAPI.rejectWithValue("Token not found in auth data");
      }

      const token = jsonData.token;

      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/stateGovernmentDefaulters`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );
        return response.data;
      } catch (apiError: any) {
        console.error("API request error (Defaulters):", apiError);

        // Handle network errors
        if (apiError.code === "ERR_NETWORK") {
          console.error("Network error - server might be down or unreachable");
          return thunkAPI.rejectWithValue(
            "Network error: Server is unreachable. Please try again later.",
          );
        }

        // Handle timeout errors
        if (apiError.code === "ECONNABORTED") {
          console.error("Request timeout - server took too long to respond");
          return thunkAPI.rejectWithValue(
            "Request timeout: Server took too long to respond. Please try again later.",
          );
        }

        // Handle other errors
        const errorMessage =
          apiError.response?.data || apiError.message || "Unknown error";
        console.error("Error details (Defaulters):", errorMessage);
        return thunkAPI.rejectWithValue(errorMessage);
      }
    } catch (error: any) {
      console.error("Unexpected error (Defaulters):", error);
      return thunkAPI.rejectWithValue(
        error.message || "An unexpected error occurred",
      );
    }
  },
);

export const approveOrder = createAsyncThunk(
  "approveOrder",
  async (data: any, thunkAPI) => {
    try {
      const auth: any = sessionStorage.getItem("authState");
      const jsonData = JSON.parse(auth);
      const token = jsonData.token;

      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/approve-order`,
        data,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );
      toast.success("Order has been approved!");

      return response.data;
    } catch (error: any) {
      if (error.response.status === 502 || error.response.status === 503) {
        toast.error("Network error");
      }
      toast.error("Something went wrong");
      return thunkAPI.rejectWithValue(error.response.data);
    }
  },
);

export const getAllBanner = createAsyncThunk(
  "getAllBanner",
  async (_, thunkAPI) => {
    try {
      const auth: any = sessionStorage.getItem("authState");
      const jsonData = JSON.parse(auth);
      const token = jsonData.token;

      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/getAllBanner`,
        {
          headers: {
            Authorization: token,
          },
        },
      );
      return res.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response.data);
    }
  },
);

export const deleteBanner = createAsyncThunk(
  "deleteBanner",
  async (id: string, thunkAPI) => {
    try {
      const auth: any = sessionStorage.getItem("authState");
      const jsonData = JSON.parse(auth);
      const token = jsonData.token;

      const response = await axios.delete(
        `${process.env.REACT_APP_API_URL}/deleteBanner/${id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response.data);
    }
  },
);

export const getAllDispatch = createAsyncThunk(
  "getAllDispatch",
  async (_, thunkAPI) => {
    try {
      const auth: any = sessionStorage.getItem("authState");
      const jsonData = JSON.parse(auth);
      const token = jsonData.token;

      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/getAllDispatcherDetails`,
        {
          headers: {
            Authorization: token,
          },
        },
      );

      return res.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response.data);
    }
  },
);

export const logisticDashboard = createAsyncThunk(
  "logisticDashboard",
  async (_, thunkAPI) => {
    try {
      const auth: any = sessionStorage.getItem("authState");
      const jsonData = JSON.parse(auth);
      const token = jsonData.token;

      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/logisticDashboardStatistic`,
        {
          headers: {
            Authorization: token,
          },
        },
      );

      return res.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response.data);
    }
  },
);

export const logisticAssignDetails = createAsyncThunk(
  "logisticAssignDetails",
  async (_, thunkAPI) => {
    try {
      const auth: any = sessionStorage.getItem("authState");
      const jsonData = JSON.parse(auth);
      const token = jsonData.token;

      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/getDetailsForLogisticAssignDelivery`,
        {
          headers: {
            Authorization: token,
          },
        },
      );

      return res.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response.data);
    }
  },
);

export const getAllVendors = createAsyncThunk(
  "getAllVendors",
  async (_, thunkAPI) => {
    const auth: any = sessionStorage.getItem("authState");
    const jsonData = JSON.parse(auth);
    const token = jsonData.token;

    try {
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/vendors`, {
        headers: {
          Authorization: token,
        },
      });
      return res.data;
    } catch (error: any) {
      console.error(error);
      return thunkAPI.rejectWithValue(error.response.data);
    }
  },
);

export const getAllStates = createAsyncThunk<
  State[],
  void,
  { rejectValue: string }
>("warehouse/getAllStates", async (_, thunkAPI) => {
  const auth: any = sessionStorage.getItem("authState");
  const jsonData = JSON.parse(auth || "{}");
  const token = jsonData?.token;

  if (!token) {
    return thunkAPI.rejectWithValue("Authentication token not found.");
  }

  try {
    const res = await axios.get(
      `${process.env.REACT_APP_API_URL}/getAllStates`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
    );
    return res.data as State[];
  } catch (error: any) {
    console.error("Error fetching states:", error);
    const errorMsg =
      error.response?.data?.error || error.message || "Failed to fetch states";
    return thunkAPI.rejectWithValue(errorMsg);
  }
});

export const getStateById = createAsyncThunk<
  State,
  string,
  { rejectValue: string }
>("warehouse/getStateById", async (stateId, thunkAPI) => {
  const auth: any = sessionStorage.getItem("authState");
  const jsonData = JSON.parse(auth || "{}");
  const token = jsonData?.token;

  if (!token) {
    return thunkAPI.rejectWithValue("Authentication token not found.");
  }

  try {
    const res = await axios.get(
      `${process.env.REACT_APP_API_URL}/getStateById/${stateId}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
    );
    return res.data as State;
  } catch (error: any) {
    console.error(`Error fetching state ${stateId}:`, error);
    const errorMsg =
      error.response?.data?.error ||
      error.message ||
      "Failed to fetch state details";

    return thunkAPI.rejectWithValue(errorMsg);
  }
});

export const updateWarehouseStore = createAsyncThunk<
  UpdateStoreResponse,
  UpdateStorePayload,
  { rejectValue: string }
>("warehouse/updateStore", async ({ stateId, storeId, data }, thunkAPI) => {
  const auth: any = sessionStorage.getItem("authState");
  const jsonData = JSON.parse(auth || "{}");
  const token = jsonData?.token;

  if (!token) {
    return thunkAPI.rejectWithValue("Authentication token not found.");
  }
  if (!stateId) {
    return thunkAPI.rejectWithValue("State ID is missing for update.");
  }
  if (!storeId) {
    return thunkAPI.rejectWithValue("Store ID is missing for update.");
  }

  console.log(
    `Thunk: Updating store. StateID: ${stateId}, StoreID: ${storeId}, Body:`,
    JSON.stringify(data, null, 2),
  );

  try {
    const response = await axios.put(
      `${process.env.REACT_APP_API_URL}/stateManagement/${stateId}/warehouse/${storeId}`,
      data,
      { headers: { Authorization: `Bearer ${token}` } },
    );

    if (response.data && response.data.updatedState) {
      console.log(
        "Thunk: Update successful, received updatedState:",
        response.data.updatedState,
      );
      return response.data.updatedState as UpdateStoreResponse;
    } else if (response.data && response.data._id && response.data.wareHouse) {
      console.log(
        "Thunk: Update successful, received State directly:",
        response.data,
      );
      return response.data as UpdateStoreResponse;
    } else {
      console.warn(
        "Thunk: Update response structure unexpected:",
        response.data,
      );
      return thunkAPI.rejectWithValue(
        "Invalid response structure from update API.",
      );
    }
  } catch (error: any) {
    console.error(
      `Error updating store (StateID: ${stateId}, StoreID: ${storeId}):`,
      error,
    );
    const errorMsg =
      error.response?.data?.error ||
      error.response?.data?.message ||
      error.message ||
      "Failed to update store";
    return thunkAPI.rejectWithValue(errorMsg);
  }
});

export const createStateAndWarehouse = createAsyncThunk<
  CreateStateResponse,
  CreateStatePayload,
  { rejectValue: string }
>("warehouse/createState", async (payload, thunkAPI) => {
  const auth: any = sessionStorage.getItem("authState");
  const jsonData = JSON.parse(auth || "{}");
  const token = jsonData?.token;

  if (!token) {
    return thunkAPI.rejectWithValue("Authentication token not found.");
  }
  if (!payload) {
    return thunkAPI.rejectWithValue("Data Cannot be empty.");
  }

  try {
    const response = await axios.post(
      `${process.env.REACT_APP_API_URL}/createStateManagement`,
      payload,
      { headers: { Authorization: `Bearer ${token}` } },
    );

    return response.data as CreateStateResponse;
  } catch (error: any) {
    console.error("Error creating state:", error);
    const errorMsg =
      error.response?.data?.error ||
      error.response?.data?.message ||
      error.message ||
      "Failed to create state";
    return thunkAPI.rejectWithValue(errorMsg);
  }
});

export const addWarehouseToState = createAsyncThunk<
  AddWarehouseResponse,
  AddWarehousePayload,
  { rejectValue: string }
>("warehouse/addStoreToState", async ({ stateId, data }, thunkAPI) => {
  const auth: any = sessionStorage.getItem("authState");
  const jsonData = JSON.parse(auth || "{}");
  const token = jsonData?.token;

  if (!token) {
    return thunkAPI.rejectWithValue("Authentication token not found.");
  }
  if (!stateId) {
    return thunkAPI.rejectWithValue("State ID is missing.");
  }

  try {
    const response = await axios.post(
      `${process.env.REACT_APP_API_URL}/stateManagement/${stateId}/warehouse`,
      data,
      { headers: { Authorization: `Bearer ${token}` } },
    );
    if (response.data && response.data.updatedState) {
      return response.data.updatedState as AddWarehouseResponse;
    } else {
      return thunkAPI.rejectWithValue(
        "Invalid response structure from add warehouse API.",
      );
    }
  } catch (error: any) {
    console.error("Error adding warehouse to state:", error);
    const errorMsg =
      error.response?.data?.error ||
      error.response?.data?.message ||
      error.message ||
      "Failed to add warehouse";
    return thunkAPI.rejectWithValue(errorMsg);
  }
});

export const getFees = createAsyncThunk("getFees", async (_, thunkAPI) => {
  const auth: any = sessionStorage.getItem("authState");
  const jsonData = JSON.parse(auth);
  const token = jsonData.token;

  try {
    const res = await axios.get(
      `${process.env.REACT_APP_API_URL}/get-allFees`,
      {
        headers: {
          Authorization: token,
        },
      },
    );
    return res.data;
  } catch (error: any) {
    console.error(error);
    return thunkAPI.rejectWithValue(error.response.data);
  }
});

export const getFlexibleUsers = createAsyncThunk(
  "getFlexibleUsers",
  async (_, thunkAPI) => {
    const auth: any = sessionStorage.getItem("authState");
    const jsonData = JSON.parse(auth);
    const token = jsonData.token;

    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/getapprovedCustomers`,
        {
          headers: {
            Authorization: token,
          },
        },
      );
      return res.data;
    } catch (error: any) {
      console.error(error);
      return thunkAPI.rejectWithValue(error.response.data);
    }
  },
);

export const getOutrightUsers = createAsyncThunk(
  "getOutrightUsers",
  async (_, thunkAPI) => {
    const auth: any = sessionStorage.getItem("authState");
    const jsonData = JSON.parse(auth);
    const token = jsonData.token;

    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/getOutrightCustomers`,
        {
          headers: {
            Authorization: token,
          },
        },
      );
      return res.data;
    } catch (error: any) {
      console.error(error);
      return thunkAPI.rejectWithValue(error.response.data);
    }
  },
);

export const getAllIncompleteUsers = createAsyncThunk(
  "getAllIncompleteUsers",
  async (_, thunkAPI) => {
    const auth: any = sessionStorage.getItem("authState");
    const jsonData = JSON.parse(auth);
    const token = jsonData.token;

    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/incompleteRegisteration`,
        {
          headers: {
            Authorization: token,
          },
        },
      );
      return res.data;
    } catch (error: any) {
      console.error(error);
      return thunkAPI.rejectWithValue(error.response.data);
    }
  },
);

export const checkTokenExpiration = () => {
  const auth: any = sessionStorage.getItem("authState");
  const jsonData = JSON.parse(auth);
  const token = jsonData?.token;

  const tokenTimestamp = sessionStorage.getItem("tokenTimestamp");
  if (auth && token && tokenTimestamp) {
    const currentTime = new Date().getTime();
    const expirationTime =
      new Date(parseInt(tokenTimestamp, 10)).getTime() + 24 * 60 * 60 * 1000;

    if (currentTime >= expirationTime) {
      sessionStorage.clear();
      sessionStorage.removeItem("tokenTimestamp");
      return false;
    }
    return true;
  }
  return false;
};

export const formatNumber = (number: number) => {
  return new Intl.NumberFormat().format(number);
};

export const getDashboardStats = async () => {
  const auth: any = sessionStorage.getItem("authState");
  const jsonData = JSON.parse(auth);
  const token = jsonData?.token;

  try {
    const response = await axios.get(
      `${process.env.REACT_APP_API_URL}/getDashboardStatistic`,
      {
        headers: {
          Authorization: token,
        },
      },
    );
    return response.data;
  } catch (error: any) {
    console.error("Failed to fetch:", error);
  }
};

export const getAdminRole = async () => {
  const authState: any = sessionStorage.getItem("authState");
  const uid = JSON.parse(authState)?.uid;
  try {
    const res = await axios.get(
      `${process.env.REACT_APP_API_URL}/get-admins-except-superadmin`,
    );
    const filteredAdmin = res.data.filter((admin: any) => admin.id === uid);
    return filteredAdmin[0].adminRole;
  } catch (error) {
    console.error("Failed to fetch:", error);
  }
};

export const verificationStageTwo = createAsyncThunk(
  "verificationStageTwo",
  async (_, thunkAPI) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/verification-stage-two`,
      );

      return response.data.customers.filter(
        (user: any) => user.accountType === "flexible",
      );
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response.data);
    }
  },
);

export const fetchStateGovernmentDetails = createAsyncThunk(
  "fetchStateGovernmentDetails",
  async (_, thunkAPI) => {
    const auth: any = sessionStorage.getItem("authState");
    const jsonData = JSON.parse(auth);
    const token = jsonData?.token;

    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/onboardingCategories`,
        {
          headers: {
            Authorization: token,
          },
        },
      );
      const data = res.data.categories.filter(
        (item: any) => item.category.toLowerCase() === "oyo state government",
      );
      return data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response.data);
    }
  },
);
